import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from '../components/Sidebar'; // adjust path
import { FaDownload, FaPlus, FaEdit, FaTrash, FaEye, FaSort,
  FaSortUp, FaSortDown, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import IssueForProductionFormModal from './IssueForProductionFormModal'; // Import the modal component
import IssueForProductionViewModal from './IssueForProductionViewModal';
import PDFDesigner from './pdfDesigner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useScreenPermissions } from '../components/useScreenPermissions';

const API_URL = '/api';

const COLORS = {
  ANGEL: '#F8FAFC',      // page background (MRN-like)
  DIVIDER: '#E6EEF2',    // divider / card border
  ACCENT: '#0f766e',
  ACCENT_HOVER: '#2b8f85',
  CARD_BORDER: '#e6eef0',
  DIVIDER1: '#2C3E50'
};

const IssueForProductionItems = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState('All');
  const [inventoryFilter, setInventoryFilter] = useState('All');
  const navigate = useNavigate();
  const [issueForProductions, setIssueForProductions] = useState([]);
  const [isPDFDesignerOpen, setIsPDFDesignerOpen] = useState(false);
  const [selectedIssueForProductionForPDF, setSelectedIssueForProductionForPDF] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedIssueForProduction, setSelectedIssueForProduction] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [stats, setStats] = useState({
    totalIssues: 0,
    withInventoryFlag: 0,
    withoutInventoryFlag: 0,
    createdThisMonth: 0
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [apiError, setApiError] = useState(null);
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  
  // --- THEME HOOKS (must run unconditionally) ---
  // Use slate-800 (#1f2937) as accent so Create New, active sidebar, logout, and profile match.
  const pageTheme = useMemo(() => ({
    bg: COLORS.ANGEL,
    sidebarBg: '#ffffff',
    accent: '#1f2937', // slate-800 (Create New button & active sidebar)
    accentHover: '#1f2937',
    border: '#e6eef0',
    text: '#0f1724',
    muted: '#6b7280',
    iconColor: '#1f2937',
    hoverBg: '#f3f4f6',
    logout: '#1f2937' // slate-800 for logout/profile requested
  }), []);
  
  // Inject CSS variables and scoped stylesheet so Sidebar matches pageTheme
  useEffect(() => {
    const themeVars = {
      '--sidebar-line-color': pageTheme.border,
      '--sidebar-bg': pageTheme.sidebarBg,
      '--sidebar-text': pageTheme.text,
      '--accent-color': pageTheme.accent,
      '--accent-hover': pageTheme.accentHover,
      '--page-border-color': pageTheme.border,
      '--page-bg': pageTheme.bg,
      '--text-primary': pageTheme.text,
      '--text-muted': pageTheme.muted,
      '--icon-color': pageTheme.iconColor,
      '--hover-bg': pageTheme.hoverBg,
      '--sidebar-active-text': pageTheme.accent,
      '--sidebar-profile-bg': pageTheme.logout,
      '--logout-bg': pageTheme.logout
    };

    Object.entries(themeVars).forEach(([k, v]) => document.documentElement.style.setProperty(k, v));

    const styleId = 'issue-sidebar-overrides';
    const existing = document.getElementById(styleId);
    if (existing) existing.remove();

    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
      /* Sidebar base: white background, black text for non-active items */
      .sidebar, .app-sidebar { background-color: var(--sidebar-bg) !important; color: var(--sidebar-text) !important; }
      .sidebar a, .sidebar .menu-item, .app-sidebar a, .app-sidebar .menu-item,
      .sidebar .nav-link, .app-sidebar .nav-link { color: var(--sidebar-text) !important; background-color: #ffffff !important; }

      /* Active item: use accent (slate-800) background with white text & icons */
      .sidebar a.active, .sidebar .nav-link.active, .sidebar .menu-item.active, .sidebar .nav-item.active,
      .app-sidebar a.active, .app-sidebar .nav-link.active {
        background-color: var(--accent-color) !important; /* slate-800 background */
        color: #ffffff !important; /* active text white and visible */
        font-weight: 700;
      }

      /* Force all nested text inside the active item to be white (covers many markup variants) */
      .sidebar a.active *,
      .sidebar .menu-item.active *,
      .sidebar .nav-link.active *,
      .app-sidebar a.active *,
      .app-sidebar .menu-item.active *,
      .app-sidebar .nav-link.active * {
        color: #ffffff !important;
        fill: #ffffff !important;
        stroke: #ffffff !important;
        opacity: 1 !important;
      }

      /* Specific common label selectors some sidebars use */
      .sidebar a.active .menu-text,
      .sidebar a.active .menu-item-text,
      .sidebar a.active .item-label,
      .sidebar .menu-item.active .menu-text,
      .sidebar .menu-item.active .menu-item-text,
      .sidebar .menu-item.active .item-label {
        color: #ffffff !important;
        opacity: 1 !important;
      }

      /* Ensure active icons also use white so they are visible on slate background */
      .sidebar a.active svg, .sidebar .nav-link.active svg, .app-sidebar a.active svg {
        color: #ffffff !important;
        fill: #ffffff !important;
      }

      /* If sidebar implementation used an inset bar, keep it subtle or remove */
      .sidebar a.active::before, .app-sidebar a.active::before {
        background: transparent !important;
      }

      /* Catch-all: if sidebar uses Tailwind classes or inline slate colors, force white text for those elements */
      .sidebar .bg-slate-800, .sidebar .bg-slate-700,
      .app-sidebar .bg-slate-800, .app-sidebar .bg-slate-700,
      .sidebar [class*="bg-slate-800"], .sidebar [class*="bg-slate-700"],
      .app-sidebar [class*="bg-slate-800"], .app-sidebar [class*="bg-slate-700"],
      .sidebar [style*="#1f2937"], .sidebar [style*="#374151"],
      .app-sidebar [style*="#1f2937"], .app-sidebar [style*="#374151"],
      .sidebar [style*="background-color: var(--accent-color)"], .app-sidebar [style*="background-color: var(--accent-color)"] {
        color: #ffffff !important;
      }

      .sidebar .bg-slate-800 *, .sidebar .bg-slate-700 *,
      .app-sidebar .bg-slate-800 *, .app-sidebar .bg-slate-700 *,
      .sidebar [class*="bg-slate-800"] *, .sidebar [class*="bg-slate-700"] *,
      .app-sidebar [class*="bg-slate-800"] *, .app-sidebar [class*="bg-slate-700"] *,
      .sidebar [style*="#1f2937"] *, .sidebar [style*="#374151"] *,
      .app-sidebar [style*="#1f2937"] *, .app-sidebar [style*="#374151"] *,
      .sidebar [style*="background-color: var(--accent-color)"] *, .app-sidebar [style*="background-color: var(--accent-color)"] * {
        color: #ffffff !important;
        fill: #ffffff !important;
        stroke: #ffffff !important;
        opacity: 1 !important;
      }

      /* Profile avatar / icon in sidebar: slate-800 background with white icon/text */
      .sidebar .profile .avatar, .sidebar .profile .icon, .sidebar .profile svg,
      .app-sidebar .profile .avatar, .app-sidebar .profile .icon, .app-sidebar .profile svg {
        background-color: var(--logout-bg) !important;
        color: #ffffff !important;
        fill: #ffffff !important;
        stroke: #ffffff !important;
      }

      /* Logout button style: slate-800 bg with white text */
      .sidebar .logout-button, .app-sidebar .logout-button,
      .sidebar .profile-action, .sidebar .logout, .app-sidebar .logout,
      .sidebar .logout-btn, .app-sidebar .logout-btn {
        background-color: var(--logout-bg) !important;
        color: #ffffff !important;
        border-radius: 4px !important;
        padding: 6px 10px !important;
      }

      /* Ensure non-active sidebar items remain white with dark text (explicitly override other styles) */
      .sidebar .menu-item, .app-sidebar .menu-item, .sidebar .nav-link, .app-sidebar .nav-link {
        background-color: #ffffff !important;
        color: var(--sidebar-text) !important;
      }

      /* Make sure hover still looks okay */
      .sidebar a:hover, .app-sidebar a:hover { background-color: rgba(0,0,0,0.03) !important; }
    `;

    document.head.appendChild(style);

    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, [pageTheme]);
  // --- end theme hooks ---

  // Fetch current user info
  useEffect(() => {
    fetchCurrentUser();
  }, []);
  
  // Fetch issue for production records
  useEffect(() => {
    if (currentUser) {
      fetchIssueForProductions();
    }
  }, [currentUser]);

   const handleLogout = () => {
    navigate('/login');
  };
  
  const downloadIssueForProductionPDF = async (issueNumber) => {
    if (!issueNumber) {
      console.error('No IssueForProduction ID provided for PDF download');
      return;
    }

    try {
      console.log(`Requesting PDF for Issue: ${issueNumber}`);
      const response = await axios.get(`${API_URL}/designs/Issue%20For%20Production/pdf/issue-for-production/${issueNumber}`, {
        responseType: 'arraybuffer'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `Issue_${issueNumber}.pdf`;
      link.click();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert(`Failed to download PDF: ${error.message}`);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      // Get the correct token
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }
      
      console.log('Fetching current user with token:', token ? 'Token found' : 'No token');
      
      const response = await fetch(`${API_URL}/current-user`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error fetching current user:', response.status, errorData);
        throw new Error(errorData.message || `Server error: ${response.status}`);
      }
      
      const userData = await response.json();
      console.log('Current user data:', userData);
      setCurrentUser(userData);
    } catch (error) {
      console.error('Error fetching current user:', error);
      // If the current-user endpoint fails, try to get user info from token directly
      try {
        const token = localStorage.getItem('authToken') || localStorage.getItem('token');
        if (token) {
          // Decode JWT token to get user info
          const payload = JSON.parse(atob(token.split('.')[1]));
          setCurrentUser({
            UserID: payload.userId || payload.sub,
            UserName: payload.email || payload.username
          });
          console.log('User info from token:', payload);
        }
      } catch (tokenError) {
        console.error('Error decoding token:', tokenError);
        setApiError('Authentication error. Please log in again.');
      }
    }
  };
  
  const fetchIssueForProductions = async () => {
    try {
      setLoading(true);
      setApiError(null);
      
      // Get the correct token
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }
      
      console.log('Fetching issue for production records with token:', token ? 'Token found' : 'No token');
      
      const response = await fetch(`${API_URL}/issue-for-production`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Server response error:', response.status, errorData);
        throw new Error(errorData.message || `Server error: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Fetched issue for production data:', data);
      setIssueForProductions(data);
      
      // Calculate statistics
      calculateStats(data);
    } catch (error) {
      console.error('Error fetching issue for production records:', error);
      setApiError(error.message || 'Failed to fetch issue for production records');
      // Set empty array to prevent rendering errors
      setIssueForProductions([]);
    } finally {
      setLoading(false);
    }
  };
  
  const calculateStats = (issueData) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    let totalIssues = issueData.length;
    let withInventoryFlag = 0;
    let withoutInventoryFlag = 0;
    let createdThisMonth = 0;
    
    issueData.forEach(issue => {
      // Count based on InventoryFlag
      if (issue.InventoryFlag === true || issue.InventoryFlag === 1) {
        withInventoryFlag++;
      } else {
        withoutInventoryFlag++;
      }
      
      // Check if created this month
      const issueDate = new Date(issue.IssueDate);
      if (issueDate.getMonth() === currentMonth && issueDate.getFullYear() === currentYear) {
        createdThisMonth++;
      }
    });
    
    setStats({
      totalIssues,
      withInventoryFlag,
      withoutInventoryFlag,
      createdThisMonth
    });
  };
  
  const handleDownloadCSV = () => {
    // Create CSV content
    let csvContent = "Issue ID,Date,Issued By,Production Order ID,Production Order Qty,Status,Inventory Flag\n";
    
    issueForProductions.forEach(issue => {
      const status = issue.Status === 'Open' ? 'Open' : 'Closed';
      const inventoryFlag = issue.InventoryFlag === 1 || issue.InventoryFlag === true ? 'Yes' : 'No';
      
      csvContent += `${issue.IssueID},${issue.IssueDate},${issue.IssuedByUserName},${issue.ProductionOrderID},${issue.ProductionOrderQty},${status},${inventoryFlag}\n`;
    });
    
    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'issue_for_production_records.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const handleCreate = () => {
    setSelectedIssueForProduction(null);
    setShowModal(true);
  };
  
  const handleEdit = (issue) => {
    setSelectedIssueForProduction(issue);
    setShowModal(true);
  };
  
  const handleView = (issue) => {
    setSelectedIssueForProduction(issue);
    setViewModalOpen(true);
  };
  
  const handleDelete = async (issueId) => {
    if (window.confirm(`Are you sure you want to delete Issue For Production ${issueId}?`)) {
      try {
        // Get the correct token
        const token = localStorage.getItem('authToken') || localStorage.getItem('token');
        
        const response = await fetch(`${API_URL}/issue-for-production/${issueId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `Server error: ${response.status}`);
        }
        
        // Refresh the list
        fetchIssueForProductions();
        alert('Issue for production record deleted successfully');
      } catch (error) {
        console.error('Error deleting issue for production record:', error);
        alert(`Error deleting issue for production record: ${error.message}`);
      }
    }
  };
  
  const handleModalSubmit = () => {
    // Refresh the list after form submission
    fetchIssueForProductions();
  };
  
  // Toggle IssueForProduction status
  const toggleIssueForProductionStatus = async (issueId, currentStatus) => {
    try {
      // Get the correct token
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      // First, get the current issue details
      const getResponse = await fetch(`${API_URL}/issue-for-production/${issueId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!getResponse.ok) {
        throw new Error('Failed to fetch issue for production details');
      }
      
      const issueData = await getResponse.json();
      
      // Then update with the new status
      const newStatus = currentStatus === 'Open' ? 'Closed' : 'Open';
      
      const response = await fetch(`${API_URL}/issue-for-production/${issueId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...issueData,
          Status: newStatus
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Server error: ${response.status}`);
      }
      
      // Refresh the list
      fetchIssueForProductions();
      alert(`Issue for production record ${newStatus === 'Open' ? 'opened' : 'closed'} successfully`);
    } catch (error) {
      console.error('Error updating issue for production status:', error);
      alert(`Error updating issue for production status: ${error.message}`);
    }
  };
  
  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };
  
  // Get sorted data
  const getSortedData = (data) => {
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      let aValue, bValue;
      
      // Handle different data types
      switch(sortConfig.key) {
        case 'IssueID':
        case 'IssuedByUserName':
        case 'ProductionOrderID':
          aValue = a[sortConfig.key] || '';
          bValue = b[sortConfig.key] || '';
          break;
        case 'IssueDate':
          aValue = new Date(a[sortConfig.key]);
          bValue = new Date(b[sortConfig.key]);
          break;
        case 'ProductionOrderQty':
          aValue = a[sortConfig.key] || 0;
          bValue = b[sortConfig.key] || 0;
          break;
        case 'Status':
        case 'InventoryFlag':
          aValue = a[sortConfig.key] ? 1 : 0;
          bValue = b[sortConfig.key] ? 1 : 0;
          break;
        default:
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
      }
      
      // Compare values
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };
  
  // Filter issue for production records based on search term, status and inventory flag
  const filteredIssueForProductions = issueForProductions.filter(issue => {
    const matchesSearch = 
      issue.IssueID.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (issue.IssuedByUserName && issue.IssuedByUserName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (issue.ProductionOrderID && issue.ProductionOrderID.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = status === 'All' || 
      (status === 'Open' && issue.Status === 'Open') || 
      (status === 'Closed' && issue.Status === 'Closed');
    
    const hasInventoryFlag = issue.InventoryFlag === 1 || issue.InventoryFlag === true;
    const matchesInventoryFlag = inventoryFilter === 'All' || 
      (inventoryFilter === 'Yes' && hasInventoryFlag) || 
      (inventoryFilter === 'No' && !hasInventoryFlag);
    
    return matchesSearch && matchesStatus && matchesInventoryFlag;
  });

  // Get sorted data
  const sortedIssueForProductions = getSortedData(filteredIssueForProductions);
  
  // Pagination logic
  const totalPages = Math.ceil(sortedIssueForProductions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = sortedIssueForProductions.slice(startIndex, endIndex);
  
  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  
  // Helper function to render sort arrows (use slate tones)
  const renderSortArrows = (columnName) => {
    const isSorted = sortConfig.key === columnName;
    const isAsc = isSorted && sortConfig.direction === 'asc';
    const isDesc = isSorted && sortConfig.direction === 'desc';

    return (
      <span className="ml-2 flex flex-col">
        <FaSortUp className={`h-3 w-3 ${isAsc ? 'text-slate-800' : 'text-slate-400'}`} />
        <FaSortDown className={`h-3 w-3 -mt-1.5 ${isDesc ? 'text-slate-800' : 'text-slate-400'}`} />
      </span>
    );
  };
  
  // Render pagination buttons (match PurchaseOrder style)
  const renderPaginationButtons = () => {
    const buttons = [];
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, currentPage + 2);

    buttons.push(
      <button
        key="prev"
        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        disabled={currentPage === 1}
        className={`p-2 transition-all duration-200 ${currentPage === 1 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-800 hover:bg-slate-100'} rounded-none`} /* sharp */
      >
        <FaChevronLeft className="h-5 w-5" />
      </button>
    );

    for (let i = start; i <= end; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-4 py-1 font-medium transition-all duration-200 ${i === currentPage ? 'bg-slate-800 text-white rounded-none' : 'text-slate-800 hover:bg-slate-700 rounded-none'}`} /* sharp */
        >
          {i}
        </button>
      );
    }

    buttons.push(
      <button
        key="next"
        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        disabled={currentPage === totalPages || totalPages === 0}
        className={`p-2 transition-all duration-200 ${currentPage === totalPages || totalPages === 0 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-800 hover:bg-slate-100'} rounded-none`} /* sharp */
      >
        <FaChevronRight className="h-5 w-5" />
      </button>
    );

    return buttons;
  };

  // Check permissions for actions
  const canCreate = hasFullAccess || hasPermission('ISSUE_FOR_PRODUCTION', 'CanCreate');
  const canView = hasFullAccess || hasPermission('ISSUE_FOR_PRODUCTION', 'CanView');
  const canEdit = hasFullAccess || hasPermission('ISSUE_FOR_PRODUCTION', 'CanEdit');
  const canDelete = hasFullAccess || hasPermission('ISSUE_FOR_PRODUCTION', 'CanDelete');
 
  if (permissionsLoading) {
    return (
      <div className="flex h-screen items-center justify-center" style={{ backgroundColor: COLORS.ANGEL }}>
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-t-4 border-b-4 border-slate-800 mx-auto rounded-full"></div>
          <p className="mt-4 text-lg font-medium text-slate-700">Loading permissions...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex" style={{
      backgroundColor: COLORS.ANGEL,
      '--accent-color': pageTheme.accent,
      '--accent-hover': pageTheme.accentHover,
      '--page-border-color': pageTheme.border,
      '--sidebar-bg': pageTheme.sidebarBg,
      '--page-background': pageTheme.bg
    }}>
      <Sidebar collapsed={sidebarCollapsed}
        onToggle={setSidebarCollapsed}
        theme={{ ...pageTheme, activeBg: pageTheme.accent, activeText: '#ffffff', profileBg: pageTheme.logout, logoutBg: pageTheme.logout }}
        onLogout={handleLogout}/>
      <main className={`flex-1 p-6 lg:p-8 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}
`}>
        {/* Header */}
        <header className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-200 pb-4">
          <div className="flex-1">
            <h1 className="text-3xl font-semibold text-slate-900">Issue For Production Records</h1>
            <p className="mt-1 text-base text-slate-600">Manage your issue for production records here.</p>
            {currentUser && (
              <p className="text-sm text-slate-500 mt-1">
                Logged in as: {currentUser.UserName} (ID: {currentUser.UserID})
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownloadCSV}
              className="flex items-center gap-2 px-4 py-2 bg-white text-slate-800 border border-slate-200 hover:bg-slate-100 font-medium transition-all duration-200 shadow-sm rounded-none"
            >
              <FaDownload className="h-4 w-4" />
              Export CSV
            </button>
            {canCreate && (
              <button
                onClick={handleCreate}
                className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white font-medium hover:bg-slate-700 transition-all duration-200 shadow-sm rounded-none"
              >
                <FaPlus className="h-4 w-4" />
                Create New
              </button>
            )}
            <button
              onClick={() => {
                if (currentItems[0]) {
                  setSelectedIssueForProductionForPDF(currentItems[0]);
                  setIsPDFDesignerOpen(true);
                }
              }}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white font-medium hover:bg-slate-700 transition-all duration-200 shadow-sm rounded-none"
            >
              <FaEdit className="h-4 w-4" />
              Design PDF
            </button>
          </div>
        </header>
        
        {/* Error message */}
        {apiError && (
          <div className="mb-6 p-4 bg-rose-50 border border-rose-200 text-rose-700 rounded-none">
            <p className="text-sm">Error loading Issue For Production: {apiError}</p>
            <div className="mt-2 flex gap-2">
              <button 
                onClick={fetchIssueForProductions}
                className="px-3 py-1 bg-rose-600 text-white rounded-none hover:bg-rose-700"
              >
                Retry
              </button>
            </div>
          </div>
        )}
        
        {/* Stats Cards */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="p-5 bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200 rounded-none">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Issues</p>
                <p className="text-2xl font-semibold text-slate-900">{stats.totalIssues}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 text-slate-800">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200 rounded-none">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">With Inventory Flag</p>
                <p className="text-2xl font-semibold text-slate-900">{stats.withInventoryFlag}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-teal-50 text-teal-700">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200 rounded-none">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Without Inventory Flag</p>
                <p className="text-2xl font-semibold text-slate-900">{stats.withoutInventoryFlag}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-50 text-amber-700">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200 rounded-none">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Created This Month</p>
                <p className="text-2xl font-semibold text-slate-900">{stats.createdThisMonth}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 text-slate-800">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>
        </section>
        
        {/* Search & Filter */}
        <section className="p-5 bg-white border border-slate-200 shadow-sm mb-6 rounded-none">
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-slate-700 mb-1">Search</label>
              <input
                type="text"
                placeholder="Search Issue ID, Issued By, Production Order ID, etc."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 bg-white border border-slate-200 text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all duration-200 rounded-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="px-4 py-2 bg-white border border-slate-200 text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all duration-200 rounded-none"
              >
                <option value="All">All</option>
                <option value="Open">Open</option>
                <option value="Closed">Closed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Inventory Flag</label>
              <select
                value={inventoryFilter}
                onChange={(e) => setInventoryFilter(e.target.value)}
                className="px-4 py-2 bg-white border border-slate-200 text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all duration-200 rounded-none"
              >
                <option value="All">All</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            <button 
              className="px-5 py-2 bg-slate-800 text-white font-medium hover:bg-slate-700 transition-all duration-200 shadow-sm rounded-none"
              onClick={() => fetchIssueForProductions()}
            >
              Search
            </button>
          </div>
        </section>
        
        {/* Table */}
        <section className="bg-white border border-slate-200 shadow-sm rounded-none">
          <div className="flex justify-between items-center p-4 border-b" style={{ borderBottomColor: COLORS.DIVIDER }}>
            <h2 className="text-xl font-semibold text-slate-900">Issue For Production Records</h2>
            <div className="flex gap-2">{renderPaginationButtons()}</div>
          </div>

          {loading ? (
            <div className="p-8 text-center text-slate-600">Loading issue for production records...</div>
          ) : sortedIssueForProductions.length === 0 ? (
            <div className="p-8 text-center text-slate-600">No issue for production records found</div>
          ) : (
            <div className="overflow-x-auto" style={{ backgroundColor: '#fff' }}>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-gray-700 text-xs font-semibold uppercase" style={{ borderBottom: `2px solid ${COLORS.DIVIDER}` }}>
                  <tr>
                    <th
                      className="px-6 py-3 text-left cursor-pointer hover:bg-slate-50 transition-colors duration-200"
                      onClick={() => handleSort('IssueID')}
                    >
                      <div className="flex items-center">
                        Issue ID
                        {renderSortArrows('IssueID')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left cursor-pointer hover:bg-slate-50 transition-colors duration-200"
                      onClick={() => handleSort('IssueDate')}
                    >
                      <div className="flex items-center">
                        Date
                        {renderSortArrows('IssueDate')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left cursor-pointer hover:bg-slate-50 transition-colors duration-200"
                      onClick={() => handleSort('IssuedByUserName')}
                    >
                      <div className="flex items-center">
                        Issued By
                        {renderSortArrows('IssuedByUserName')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left cursor-pointer hover:bg-slate-50 transition-colors duration-200"
                      onClick={() => handleSort('ProductionOrderID')}
                    >
                      <div className="flex items-center">
                        Production Order ID
                        {renderSortArrows('ProductionOrderID')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left cursor-pointer hover:bg-slate-50 transition-colors duration-200"
                      onClick={() => handleSort('ProductionOrderQty')}
                    >
                      <div className="flex items-center">
                        Production Order Qty
                        {renderSortArrows('ProductionOrderQty')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left cursor-pointer hover:bg-slate-50 transition-colors duration-200"
                      onClick={() => handleSort('Status')}
                    >
                      <div className="flex items-center">
                        Status
                        {renderSortArrows('Status')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left cursor-pointer hover:bg-slate-50 transition-colors duration-200"
                      onClick={() => handleSort('InventoryFlag')}
                    >
                      <div className="flex items-center">
                        Inventory Flag
                        {renderSortArrows('InventoryFlag')}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: COLORS.DIVIDER }}>
                  {currentItems.map((issue) => {
                    const hasInventoryFlag = issue.InventoryFlag === 1 || issue.InventoryFlag === true;
                    const inventoryFlagText = hasInventoryFlag ? 'Yes' : 'No';
                    const isOpen = issue.Status === 'Open';
                    const statusText = issue.Status || '-';
                    
                    return (
                      <tr key={issue.IssueID} className="hover:bg-slate-50 transition-colors duration-200">
                        <td className="px-6 py-3 font-medium text-slate-900 uppercase">{issue.IssueID}</td>
                        <td className="px-6 py-3 text-slate-700">{new Date(issue.IssueDate).toLocaleDateString()}</td>
                        <td className="px-6 py-3 text-slate-700 uppercase">{issue.IssuedByUserName || '-'}</td>
                        <td className="px-6 py-3 text-slate-700 uppercase">{issue.ProductionOrderID || '-'}</td>
                        <td className="px-6 py-3 text-slate-700">{issue.ProductionOrderQty || 0}</td>
                        <td className="px-6 py-3">
                          <span 
                            className={`inline-block px-3 py-1 text-xs font-medium cursor-pointer transition-colors duration-200 ${
                              isOpen ? 'bg-teal-50 text-teal-700 hover:bg-teal-100' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                            } rounded-full`}
                            onClick={() => toggleIssueForProductionStatus(issue.IssueID, issue.Status)}
                            title={`Click to ${isOpen ? 'close' : 'open'}`}
                          >
                            {statusText}
                          </span>
                        </td>
                        <td className="px-6 py-3">
                          <span 
                            className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase ${
                              hasInventoryFlag 
                                ? 'bg-slate-100 text-slate-800' 
                                : 'bg-slate-50 text-slate-700'
                            }`}
                          >
                            {inventoryFlagText}
                          </span>
                        </td>
                        <td className="px-6 py-3">
                          <div className="flex gap-3 justify-center">
                            {canView && (
                              <button 
                                onClick={() => handleView(issue)}
                                className="text-slate-800 hover:text-slate-900 transition-colors duration-200 rounded-none p-1"
                                title="View"
                              >
                                <FaEye className="h-4 w-4" />
                              </button>
                            )}
                            {canEdit && (
                              <button 
                                onClick={() => handleEdit(issue)}
                                className="text-teal-700 hover:text-teal-900 transition-colors duration-200 rounded-none p-1"
                                title="Edit"
                              >
                                <FaEdit className="h-4 w-4" />
                              </button>
                            )}
                            <button 
                              onClick={() => downloadIssueForProductionPDF(issue.IssueID)} 
                              className="text-slate-700 hover:text-slate-900 transition-colors duration-200 rounded-none p-1"
                              title="Download PDF"
                            >
                              <FaDownload className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
        
        {/* IssueForProduction Form Modal */}
        <IssueForProductionFormModal
          open={showModal}
          onClose={() => setShowModal(false)}
          onSubmit={handleModalSubmit}
          initialData={selectedIssueForProduction}
          isEdit={!!selectedIssueForProduction}
          currentUser={currentUser}
        />
        
        <IssueForProductionViewModal
          open={viewModalOpen}
          onClose={() => setViewModalOpen(false)}
          issueId={selectedIssueForProduction}
        />
        
        {/* PDF Designer Modal */}
        {isPDFDesignerOpen && selectedIssueForProductionForPDF && (
          <PDFDesigner
            isOpen={isPDFDesignerOpen}
            onClose={() => setIsPDFDesignerOpen(false)}
            entity="Issue For Production"
            sampleData={{
              IssueID: selectedIssueForProductionForPDF.IssueID || '-',
              IssueDate: selectedIssueForProductionForPDF.IssueDate ? new Date(selectedIssueForProductionForPDF.IssueDate).toLocaleDateString() : '-',
              IssuedByUserID: selectedIssueForProductionForPDF.IssuedByUserID || '-',
              IssuedByUserName: selectedIssueForProductionForPDF.IssuedByUserName || '-',
              ProductionOrderID: selectedIssueForProductionForPDF.ProductionOrderID || '-',
              ProductionOrderQty: selectedIssueForProductionForPDF.ProductionOrderQty || 0,
              BranchCode: selectedIssueForProductionForPDF.BranchCode || '-',
              BranchName: selectedIssueForProductionForPDF.BranchName || '-',
              Status: selectedIssueForProductionForPDF.Status || '-',
              InventoryFlag: selectedIssueForProductionForPDF.InventoryFlag === 1 || selectedIssueForProductionForPDF.InventoryFlag === true ? 'Yes' : 'No',

              Details: (selectedIssueForProductionForPDF.Details || []).map(detail => ({
                ProductCode: detail.ProductCode || '-',
                ProductName: detail.ProductName || '-',
                MaterialType: detail.MaterialType || '-',
                UOM: detail.UOM || '-',
                Qty: detail.Qty || 0,
                CalculatedQty: detail.CalculatedQty || 0,
                FlushType: detail.FlushType || '-',
                Warehouse: detail.Warehouse || '-',
                BatchNumber: detail.BatchNumber || '-',
                ExpiryDate: detail.ExpiryDate || '-',
              })),

              Summary: {
                TotalQty: selectedIssueForProductionForPDF.ProductionOrderQty || 0,
                TotalCalculatedQty: (selectedIssueForProductionForPDF.Details || []).reduce((sum, d) => sum + (d.CalculatedQty || 0), 0)
              }
            }}
            availableFields={{
              Header: [
                "IssueID", "IssueDate", "IssuedByUserID", "IssuedByUserName",
                "ProductionOrderID", "ProductionOrderQty", "BranchCode", "BranchName",
                "Status", "InventoryFlag"
              ],
              Details: [
                "ProductCode", "ProductName", "MaterialType", "UOM", "Qty",
                "CalculatedQty", "FlushType", "Warehouse", "BatchNumber", "ExpiryDate"
              ],
              Summary: [
                "TotalQty", "TotalCalculatedQty"
              ]
            }}
          />
        )}
      </main>
    </div>
  );
};

export default IssueForProductionItems;
